/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion, max-lines-per-function */
import { Color } from '@dreryk/color-common-api';
import {
   PractitionerReceptionServiceParameters,
   ReceptionServiceAlreadyExistsFault,
   ReceptionServiceUpdateCommand,
} from '@dreryk/egabinet-private-medical-service-frontend-api';
import { Money } from '@dreryk/money-common-api';
import { ReceptionServiceDuration } from '@dreryk/egabinet-private-medical-service-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { receptionServiceTranslationKeys } from '../../translations';
import { ReceptionServicePractitionerValue } from '../shared/form/components/practitioner/receptionServicePractitioner.types';
import { receptionServicePractitionerConfigurationVisitor } from '../shared/form/components/practitioner/receptionServicePractitioner.utils';
import {
   receptionServicePaymentTypeVisitor,
   receptionServicePriceVisitor,
} from '../shared/form/components/price/receptionServicePrice.utils';
import { receptionServiceLocationVisitor } from '../shared/form/components/serviceLocations/serviceLocations.utils';

import { ReceptionServiceUpdateFormBean } from './ReceptionServiceUpdateForm';

type ReceptionServiceUpdateActionRenderProps = {
   updateReceptionService: (formData: ReceptionServiceUpdateFormBean) => Promise<void>;
};

type ReceptionServiceUpdateActionProps = {
   children: (renderProps: ReceptionServiceUpdateActionRenderProps) => JSX.Element;
   onSuccess: VoidFunction;
   onReceptionServiceAlreadyExistsFault: VoidFunction;
};

// eslint-disable-next-line max-lines-per-function
export const ReceptionServiceUpdateAction = (props: ReceptionServiceUpdateActionProps): JSX.Element => {
   const { executeAsync } = useCqrsCommand<void, ReceptionServiceUpdateFormBean, ReceptionServiceAlreadyExistsFault>(
      (parameters) => {
         const command = new ReceptionServiceUpdateCommand();
         command.id = parameters.id;
         command.name = parameters.name!;
         command.estimatedDuration = ReceptionServiceDuration.ofMinutes(parameters.estimatedDuration!);
         command.additionalInformation = Choice.withUseDefaultIfNull(parameters.additionalInformation);
         command.color = Choice.withUseDefaultIfNull(parameters.color ? Color.of(parameters.color) : null);
         command.deliveryMode = parameters.videoConsultation === true ? 'VIDEO_CONSULTATION' : 'ON_SITE';

         receptionServicePriceVisitor(parameters)({
            asFree: () => {
               command.minPrice = Money.of(0);
               command.maxPrice = Money.of(0);
            },
            withPrice: ({ price }) => {
               command.minPrice = Money.of(price!);
               command.maxPrice = Money.of(price!);
            },
            withPriceRange: ({ priceRange }) => {
               command.minPrice = Money.of(priceRange!.min);
               command.maxPrice = Money.of(priceRange!.max);
            },
         });

         receptionServicePaymentTypeVisitor(parameters, {
            withAdvancePayment: ({ advancePaymentAmount }) => {
               command.advancePayment = Choice.of({
                  value: Money.of(advancePaymentAmount),
               });
            },
         });

         command.practitioners = (() => {
            const mapPractitioner = (
               practitioner: ReceptionServicePractitionerValue
            ): PractitionerReceptionServiceParameters => {
               if (parameters.freeChecked) {
                  if (practitioner.usingDefaultConfiguration) {
                     return {
                        practitionerId: practitioner.id,
                        usingDefaultConfiguration: true,
                     };
                  }

                  return {
                     practitionerId: practitioner.id,
                     usingDefaultConfiguration: false,
                     estimatedDuration: Choice.of(ReceptionServiceDuration.ofMinutes(practitioner.estimatedDuration!)),
                     minPrice: Choice.of(Money.of(0)),
                     maxPrice: Choice.of(Money.of(0)),
                  };
               }

               return receptionServicePractitionerConfigurationVisitor(
                  practitioner
               )<PractitionerReceptionServiceParameters>({
                  withDefaultConfiguration: ({ usingDefaultConfiguration }) => ({
                     practitionerId: practitioner.id,
                     usingDefaultConfiguration: usingDefaultConfiguration,
                  }),
                  withIndividualPrice: ({ individualPrice, usingDefaultConfiguration }) => ({
                     practitionerId: practitioner.id,
                     usingDefaultConfiguration: usingDefaultConfiguration,
                     minPrice: Choice.of(Money.of(individualPrice!)),
                     maxPrice: Choice.of(Money.of(individualPrice!)),
                     estimatedDuration: Choice.of(ReceptionServiceDuration.ofMinutes(practitioner.estimatedDuration!)),
                  }),
                  withIndividualPriceRange: ({ individualPriceRange, usingDefaultConfiguration }) => ({
                     practitionerId: practitioner.id,
                     usingDefaultConfiguration: usingDefaultConfiguration,
                     minPrice: Choice.of(Money.of(individualPriceRange!.min)),
                     maxPrice: Choice.of(Money.of(individualPriceRange!.max)),
                     estimatedDuration: Choice.of(ReceptionServiceDuration.ofMinutes(practitioner.estimatedDuration!)),
                  }),
               })!;
            };

            if (parameters.practitioner) {
               return [mapPractitioner(parameters.practitioner)];
            }

            return parameters.practitioners.value!.map(mapPractitioner);
         })();

         command.serviceLocationIds = receptionServiceLocationVisitor(parameters)({
            withSingleServiceLocation: (serviceLocation) => [serviceLocation!.id],
            withMultipleServiceLocations: (serviceLocations) =>
               serviceLocations.map((serviceLocation) => serviceLocation.id),
         });

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(receptionServiceTranslationKeys.update.modal.confirmationText);
            props.onSuccess();
         },
         onFault: {
            'egabinet-private-medical-service-frontend-api:ReceptionServiceAlreadyExistsFault':
               props.onReceptionServiceAlreadyExistsFault,
         },
      }
   );

   return props.children({
      updateReceptionService: executeAsync,
   });
};
